import ScannerInput from 'src/shared/scanning/ScannerInput';
import useScanner from 'src/shared/scanning/useScanner';
import type { ScannerEvent } from 'src/shared/scanning/useScanner';
import { findScanMatches } from 'src/shared/scanning/scanClassifier';
import { ReviewTextField } from 'src/core/forms/FieldReview';
import { useMessageContext } from 'src/shared/support/MessageContext';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CustomButton from 'src/core/ui/CustomButton';
import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useGetDispatchOrderByPickingList } from 'src/pages/warehouses/warehouseRequests';

const DispatchOrderScanner = ({ disabled, dispatchOrderId, pickingListId, onVerificationStarted, warehouseId, orderStatus, orderStatusOptions, readyToDispatch, dispatchOrderRows, setDispatchOrderRows, enterDispatchedRowQtyLimit, onDispatchOrderScan, onDispatch, onCancel }) => {
    const [verificationScan, setVerificationScan] = useState<{ productId: string; key: number } | null>(null);
    const verificationSequence = useRef(0);
    const verificationPending = useRef(false);
    const { updateMessageStatus } = useMessageContext();
    const warn = (message: string) => updateMessageStatus({ requestUrl: '/scanning/dispatch-orders', status: 'error', message });
    const pendingWithoutPickingList = Number(orderStatus) === 4 && !pickingListId;
    const scanningBlocked = disabled || verificationScan != null;

    const [quantityRow, setQuantityRow] = useState<any>(null);
    const [manualQuantity, setManualQuantity] = useState<string>('');
    const [quantityError, setQuantityError] = useState<string>('');
    const quantityInputRef = useRef<HTMLInputElement>(null);
    const scanner = useScanner({ id: 'dispatch-orders', onScan: scan => handleScannerSubmit(scan), disabled: scanningBlocked || quantityRow != null, input: { ariaLabel: 'Scan dispatch order, picking list or product', placeholder: 'Scan Dispatch Order, Picking List Or Product' }, submitButton: { visible: quantityRow == null }, focus: { autoFocus: true, restore: true, restoreOn: [quantityRow] } });

    useEffect(() => {
        if (scanningBlocked) return;
        if (quantityRow) {
            quantityInputRef.current?.focus();
            quantityInputRef.current?.select();
        } else {
            scanner.focus();
        }
    }, [quantityRow, scanningBlocked]);

    // Discard a pending entry if the order's rows have been replaced.
    useEffect(() => {
        if (quantityRow && !dispatchOrderRows?.includes(quantityRow)) {
            setQuantityRow(null);
            setManualQuantity('');
            setQuantityError('');
        }
    }, [dispatchOrderRows, quantityRow]);

    const closeQuantityEntry = () => {
        setQuantityRow(null);
        setManualQuantity('');
        setQuantityError('');
    };

    const handleQuantitySubmit = () => {
        if (disabled || verificationPending.current) return;
        if (pendingWithoutPickingList) {
            warn('Print the picking list first before updating product quantities.');
            closeQuantityEntry();
            return;
        }
        if (!quantityRow || !dispatchOrderRows?.includes(quantityRow)) {
            closeQuantityEntry();
            return;
        }

        const quantity = Number(manualQuantity);
        const orderedQuantity = Number(quantityRow.OrderedQty ?? 0);
        if (!manualQuantity.trim() || !Number.isFinite(quantity) || quantity <= 0 || quantity > orderedQuantity) {
            setQuantityError(`Enter a quantity greater than 0 and no more than ${orderedQuantity}.`);
            quantityInputRef.current?.focus();
            return;
        }

        // The entered value is the row's dispatched total, not an increment.
        setDispatchOrderRows((currentRows) => currentRows.map((row) => row === quantityRow ? { ...row, DispatchedQty: quantity } : row
        ));
        closeQuantityEntry();
    };

    const dispatchOrderStatus = readyToDispatch
        ? 'Ready to dispatch'
        : orderStatusOptions?.find((option) => option.value === orderStatus)?.label ?? orderStatus ?? '-';

    const applyProductScan = (submittedValue: string) => {
        const targetRow = dispatchOrderRows.find((row) => findScanMatches({ rows: [row], value: submittedValue, getIdentifiers: item => [item.ProductId, item.Barcode, item.BarCode] }).length > 0 &&
            Number(row.DispatchedQty ?? 0) < Number(row.OrderedQty ?? 0)
        );
        if (!targetRow) return;

        const quantityLimit = Number(enterDispatchedRowQtyLimit);
        if (enterDispatchedRowQtyLimit != null && enterDispatchedRowQtyLimit !== '' &&
            Number.isFinite(quantityLimit) && quantityLimit >= 0 &&
            Number(targetRow.OrderedQty ?? 0) > quantityLimit) {
            setQuantityRow(targetRow);
            setManualQuantity('');
            setQuantityError('');
            return;
        }

        setDispatchOrderRows((currentRows) => currentRows.map((row) => row === targetRow
            ? { ...row, DispatchedQty: Math.min(Number(row.OrderedQty ?? 0), Number(row.DispatchedQty ?? 0) + 1) }
            : row
        ));
    };

    const handleScannerSubmit = ({ value: submittedValue, command }: ScannerEvent) => {
        if (disabled || verificationPending.current) return;
        if (quantityRow) {
            handleQuantitySubmit();
            return;
        }

        if (command === 'cancel') {
            closeQuantityEntry();
            setVerificationScan(null);
            verificationPending.current = false;
            onCancel();
            return;
        }
        if (command === 'ok') {
            if (readyToDispatch) onDispatch();
            return;
        }

        const productExists = dispatchOrderRows?.some((row) => findScanMatches({ rows: [row], value: submittedValue, getIdentifiers: item => [item.ProductId, item.Barcode, item.BarCode] }).length > 0);

        if (productExists) {
            if (pendingWithoutPickingList) {
                warn('Print the picking list first before scanning products or updating quantities.');
                return;
            }
            if (Number(orderStatus) === 4) {
                verificationPending.current = true;
                setVerificationScan({ productId: submittedValue, key: ++verificationSequence.current });
                return;
            }
            applyProductScan(submittedValue);
            return;
        }

        onDispatchOrderScan(submittedValue);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {verificationScan && <StartDispatchOrderVerification key={verificationScan.key} pickingListId={pickingListId} warehouseId={warehouseId} dispatchOrderId={dispatchOrderId} onResolved={(success: boolean) => {
                verificationPending.current = false;
                setVerificationScan(null);
                if (disabled) return;
                if (!success) {
                    warn('Could not start verification. Scan the product again to retry.');
                    return;
                }
                onVerificationStarted();
                applyProductScan(verificationScan.productId);
            }} />}
            {verificationScan && <Typography variant="caption">Starting verification…</Typography>}
            <ScannerInput scanner={scanner} />

            {quantityRow && (
                <ReviewTextField disabled={scanningBlocked || pendingWithoutPickingList} inputRef={quantityInputRef} label="Dispatched quantity" type="number" value={manualQuantity} onChange={(event) => {
                    setManualQuantity(event.target.value);
                    setQuantityError('');
                }} onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.nativeEvent.isComposing && !event.repeat) {
                        event.preventDefault();
                        event.stopPropagation();
                        handleQuantitySubmit();
                    } else if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        closeQuantityEntry();
                    }
                }} inputProps={{ min: 0, max: Number(quantityRow.OrderedQty ?? 0), step: 'any' }} error={Boolean(quantityError)} helperText={quantityError || `Total for product ${quantityRow.ProductId} (max ${quantityRow.OrderedQty})`} sx={{ width: { xs: 180, sm: 240 } }} />
            )}

            {quantityRow && <CustomButton disabled={scanningBlocked || pendingWithoutPickingList} title="Apply quantity" size="small" iconButton variant="outlined" icon={<ArrowForwardIcon />} onClick={handleQuantitySubmit} />}
            {quantityRow && <CustomButton title="Cancel" size="small" variant="outlined" onClick={closeQuantityEntry} />}

            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" noWrap>Warehouse ID: {warehouseId ?? '-'}</Typography>
                <Typography variant="caption" display="block" noWrap sx={{ color: readyToDispatch ? 'success.main' : 'inherit', fontWeight: readyToDispatch ? 600 : 'inherit' }}>Status: {dispatchOrderStatus}</Typography>
            </Box>
        </Box>
    );
};

// Use only the picking-list lookup here: looking up by order ID does not start verification.
const StartDispatchOrderVerification = ({ pickingListId, warehouseId, dispatchOrderId, onResolved }) => {
    const { rows: response, status }: any = useGetDispatchOrderByPickingList(pickingListId, warehouseId);
    const hasResolved = useRef(false);
    const onResolvedRef = useRef(onResolved);
    onResolvedRef.current = onResolved;

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            if (hasResolved.current) return;
            hasResolved.current = true;
            onResolvedRef.current(false);
        }, 15000);
        return () => window.clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        if (hasResolved.current) return;
        if (status === 'error' || status === 'aborted' || Number(status) >= 400) {
            hasResolved.current = true;
            onResolvedRef.current(false);
            return;
        }
        if (!requestSucceeded(status)) return;
        const order = Array.isArray(response) ? response[0] : response;
        if (!order?.DispatchOrderId) return;
        hasResolved.current = true;
        onResolvedRef.current(String(order.DispatchOrderId) === String(dispatchOrderId) && Number(order.OrderStatus?.Key ?? order.OrderStatus) === 5);
    }, [response, status, dispatchOrderId]);

    return null;
};

const requestSucceeded = (status: unknown) => status === 'success' || (Number(status) >= 200 && Number(status) < 300);

export default DispatchOrderScanner;
